// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import cx from 'classnames';

import { getTestAttr } from '@shared/utils/common';
import WarningIcon from '@shared/icons/Warning';
import Button from '@shared/components/Button';
import Flex from '@shared/components/Flex';
import FileUpload, { FileUploadProps } from '@shared/components/FileUpload';
import { alpha } from '@mui/material';

export interface ResultProps {
  className?: string;
  data: ValidationData;
  uploadProps: Omit<FileUploadProps, 'onUpload' | 'classes'>;
  onValidate: (file: File) => any;
  onUpload: () => any;
}

export interface ValidationData {
  hasError: boolean;
  statuses: Array<{ key: string; fields: Array<string> }>;
  title?: string;
}

class Result extends React.Component<ResultProps> {
  render() {
    const { data, uploadProps, onValidate, onUpload } = this.props;
    const classes = withStyles.getClasses(this.props);
    const errorText = 'Please reupload.';

    return (
      <Flex
        direction="column"
        justifyContent="space-between"
        className={classes.root}
      >
        <div
          className={classes.block}
        >
          <h6 className={classes.title}>{data.title}</h6>
          {data.statuses.map((status) => {
            return (
              <div
                key={status.key}
                className={classes.fieldsContainer}
              >
                <div
                  className={cx(
                    classes.status,
                    {
                      [classes.invalidStatus]: data.hasError,
                    }
                  )}
                >
                  {status.key}
                </div>
                <div className={classes.fields}>
                  {status.fields.map((field) => <p key={field} className={classes.field}>{field}</p>)}
                </div>
              </div>
            );
          })}
        </div>
        <Flex
          alignItems="center"
          wrap="nowrap"
          justifyContent="space-between"
        >
          <Button
            {...getTestAttr('Import-data', 'finish', 'btn')}
            disabled={data.hasError}
            text="Finish"
            color="secondary"
            variant="contained"
            size="large"
            onClick={onUpload}
          />
          {data.hasError && (
            <Flex
              alignItems="center"
              className={classes.invalidBlock}
            >
              <WarningIcon />
              <span className={classes.invalidBlockText}>{errorText}</span>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                className={classes.importBtn}
              >
                <FileUpload
                  {...uploadProps}
                  placeholder="Browse & import"
                  classes={{
                    root: classes.fileUploadRoot,
                    placeholder: classes.fileUploadPlaceholder
                  }}
                  onUpload={onValidate}
                />
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  }
}

const ResultStyled = withStyles(
  Result, 
  (theme, props) => ({
    "root": {
      height: '100%',
    },
    "block": {
      overflowY: 'auto',
      maxHeight: 'calc(100% - 62px)',
    },
    "invalidBlock": {
      padding: theme.spacing(2, 4),
      backgroundColor: `${alpha(theme.palette.colors.error, .08)}`,
      border: `1px solid ${theme.palette.colors.error}`,
      borderRadius: '4px',

      '& svg': {
        fontSize: 14,
        marginRight: theme.spacing(2),
      }
    },
    "invalidBlockText": {
      color: theme.palette.colors.error,
      fontSize: 13,
    },
    "title": {
      fontSize: 22,
      fontWeight: 600,
    },
    "status": {
      color: theme.palette.colors.green,
      fontSize: 13,
    },
    "invalidStatus": {
      color: theme.palette.colors.error,
    },
    "fieldsContainer": {
      marginTop: theme.spacing(),
    },
    "fields": {
      marginTop: theme.spacing(),
    },
    "field": {
      color: theme.palette.colors.greyDark,
    },
    "importBtn": {
      position: 'relative',
      width: 131,
      height: 32,
      overflow: 'hidden',
      border: `1px solid ${theme.palette.colors.primaryAccent}`,
      marginLeft: theme.spacing(4),
    },
    "fileUploadRoot": {
      position: 'absolute',
      width: '100%',
      height: '100%',
      backgroundColor: theme.palette.colors.white,
      border: 'none',
    },
    "fileUploadPlaceholder": {
      color: theme.palette.colors.primaryAccent,
      fontWeight: 500,
      fontSize: 12,
    }
  })
);

export default ResultStyled;